import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Eye,
  KeyRound,
  RefreshCw,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ModalOverlay } from "@/components/ModalOverlay";
import { PaginatedList } from "@/components/PaginatedList";
import { SchemaForm } from "@/components/SchemaForm";
import { usePartnerScope } from "@/components/partner-scope-context";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { usePaginatedList } from "@/lib/pagination";
import { hasWriteOn, isSuperAdmin } from "@/lib/roles";
import { buildValuesFromSchema, hasProperties } from "@/lib/schema-form";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import type {
  ProviderCatalogEntry,
  ProviderCredential,
  ProviderCredentialCreateRequest,
  ProviderCredentialRotateRequest,
  ProviderEnvironment,
  Partner,
} from "@tilt/admin-sdk";

const ENVIRONMENTS: ProviderEnvironment[] = ["sandbox", "production"];

const ENV_LABEL: Record<ProviderEnvironment, string> = {
  sandbox: "Sandbox",
  production: "Production",
};

export function ProviderCredentialsPage(): JSX.Element {
  usePageTitle("Provider credentials");
  const queryClient = useQueryClient();
  const { scope } = usePartnerScope();
  const [createOpen, setCreateOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState<ProviderCredential | null>(null);
  const [revealTarget, setRevealTarget] = useState<ProviderCredential | null>(null);
  const [bindingsTarget, setBindingsTarget] = useState<ProviderCredential | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [providerFilter, setProviderFilter] = useState("");
  const [environmentFilter, setEnvironmentFilter] = useState<"" | ProviderEnvironment>("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [sort, setSort] = useState<SortState>({ col: "last_rotated_at", dir: "desc" });

  // Credentials are scoped to a partner subtree; the owning row is a
  // partner (post-V0012) rather than a customer_org. Pull the
  // full partner list and filter to non-deleted, non-platform rows so
  // the picker shows the same set as the Partners page.
  const partners = useQuery({
    queryKey: ["partners", "for-credentials", scope.id],
    queryFn: () => apiClient.listPartners(),
    retry: false,
  });
  const ownerPartners = useMemo(
    () =>
      (partners.data?.partners ?? []).filter((t) => !t.is_deleted && t.partner_type !== "platform"),
    [partners.data],
  );

  // Drive the provider dropdown + per-field rendering off the platform
  // catalog. A new provider slug + credential_schema row appears here
  // automatically - no code change required to support it.
  const providerCatalog = useQuery({
    queryKey: ["provider-catalog"],
    queryFn: () => apiClient.listProviderCatalog(),
    staleTime: 60_000,
  });
  // Full catalog (enabled only) - drives the filter dropdown above.
  const catalogProviders: ProviderCatalogEntry[] = useMemo(
    () => (providerCatalog.data?.providers ?? []).filter((p) => p.is_enabled),
    [providerCatalog.data],
  );
  // Credential-requiring subset - drives the New/Rotate dialogs. Cash
  // and Check (and any future journal-entry providers) have empty
  // credential_schemas; showing them in a "New credential" dropdown
  // would dump the user onto a raw JSON textarea with no useful
  // fields. Filter them out here so only providers with real
  // credential fields are pickable.
  const credRequiringProviders: ProviderCatalogEntry[] = useMemo(
    () => catalogProviders.filter((p) => hasProperties(p.credential_schema)),
    [catalogProviders],
  );

  const credsList = usePaginatedList({
    queryKey: [
      "provider-credentials",
      scope.id,
      { showDeleted, providerFilter, environmentFilter, partnerFilter },
    ],
    fetcher: ({ limit, offset, q }) =>
      apiClient
        .listProviderCredentials({
          limit,
          offset,
          ...(q ? { q } : {}),
          scope_path: scope.path,
          include_deleted: showDeleted,
          ...(providerFilter ? { provider: providerFilter } : {}),
          ...(environmentFilter ? { environment: environmentFilter } : {}),
          ...(partnerFilter ? { partner_id: partnerFilter } : {}),
        })
        .then((res) => ({
          items: res.credentials,
          total: res.total,
          limit: res.limit,
          offset: res.offset,
          has_more: res.has_more,
        })),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["provider-credentials"] });
  };

  // "New credential" is gated on whether ANY partner in the picker
  // list is write-accessible to this caller. A partner_viewer on
  // Finterra sees the partner in the list but can't create under it,
  // so the button would 403 on every submit - hide it outright.
  const profile = useRoleProfile();
  const writableOwnerPartners = useMemo(
    () => ownerPartners.filter((t) => hasWriteOn(profile, t.path)),
    [ownerPartners, profile],
  );
  const canCreate = isSuperAdmin(profile) || writableOwnerPartners.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Credentials</h1>
          <p className="text-sm text-muted-foreground">
            Payment-provider keys and tokens for your partners. Secrets stay encrypted - use Rotate
            to swap a set without downtime.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            disabled={writableOwnerPartners.length === 0}
            title={writableOwnerPartners.length === 0 ? "No partners in scope" : "New credential"}
            className="flex flex-shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-40"
          >
            <KeyRound className="h-4 w-4" />
            New credential
          </button>
        )}
      </div>

      <PaginatedList
        state={credsList}
        itemNoun="credentials"
        searchPlaceholder="Search by label, provider, or partner…"
        filters={
          <>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Partner
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                <option value="">All in scope</option>
                {ownerPartners.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Provider
              <select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                <option value="">Any</option>
                {catalogProviders.map((p) => (
                  <option key={p.id} value={p.slug}>
                    {p.display_name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Environment
              <select
                value={environmentFilter}
                onChange={(e) => setEnvironmentFilter(e.target.value as typeof environmentFilter)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                <option value="">Any</option>
                {ENVIRONMENTS.map((env) => (
                  <option key={env} value={env}>
                    {ENV_LABEL[env]}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Show deleted
            </label>
          </>
        }
        emptyState={
          <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            No provider credentials configured yet.
          </div>
        }
      >
        {credsList.query.isError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {credsList.query.error instanceof Error
              ? credsList.query.error.message
              : "Failed to load credentials"}
          </div>
        )}
        {credsList.items.length > 0 && (
          <CredentialsTable
            items={sortCredentials(credsList.items, sort)}
            sort={sort}
            onSort={setSort}
            onRotate={setRotateTarget}
            onReveal={setRevealTarget}
            onOpenBindings={setBindingsTarget}
            onMutated={invalidate}
          />
        )}
      </PaginatedList>

      {bindingsTarget && (
        <BindingsDrawer cred={bindingsTarget} onClose={() => setBindingsTarget(null)} />
      )}

      {createOpen && (
        <CreateDialog
          partners={ownerPartners}
          defaultPartnerId={
            partnerFilter ||
            (ownerPartners.some((t) => t.id === scope.id) ? scope.id : "") ||
            ownerPartners[0]?.id ||
            ""
          }
          providers={credRequiringProviders}
          onClose={() => setCreateOpen(false)}
          onMutated={() => {
            setCreateOpen(false);
            invalidate();
          }}
        />
      )}
      {rotateTarget && (
        <RotateDialog
          cred={rotateTarget}
          providers={credRequiringProviders}
          onClose={() => setRotateTarget(null)}
          onMutated={() => {
            setRotateTarget(null);
            invalidate();
          }}
        />
      )}
      {revealTarget && <RevealDialog cred={revealTarget} onClose={() => setRevealTarget(null)} />}
    </div>
  );
}

// --------------------------------------------------------------------- //
// Table (push 14 - dense replaces card grid)
// --------------------------------------------------------------------- //

type SortColumn = "label" | "provider" | "environment" | "partner_path" | "last_rotated_at";
type SortState = { col: SortColumn; dir: "asc" | "desc" };

function sortCredentials(items: ProviderCredential[], sort: SortState): ProviderCredential[] {
  const sign = sort.dir === "asc" ? 1 : -1;
  const key = (c: ProviderCredential): string => {
    switch (sort.col) {
      case "label":
        return c.label.toLowerCase();
      case "provider":
        return c.provider;
      case "environment":
        return c.environment;
      case "partner_path":
        return c.partner.path;
      case "last_rotated_at":
        return c.last_rotated_at ?? "";
    }
  };
  return [...items].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    if (ka < kb) return -1 * sign;
    if (ka > kb) return 1 * sign;
    return 0;
  });
}

function CredentialsTable(props: {
  items: ProviderCredential[];
  sort: SortState;
  onSort: (s: SortState) => void;
  onRotate: (c: ProviderCredential) => void;
  onReveal: (c: ProviderCredential) => void;
  onOpenBindings: (c: ProviderCredential) => void;
  onMutated: () => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
          <tr>
            <SortHeader label="Label" col="label" sort={props.sort} onSort={props.onSort} />
            <SortHeader label="Provider" col="provider" sort={props.sort} onSort={props.onSort} />
            <SortHeader label="Env" col="environment" sort={props.sort} onSort={props.onSort} />
            <SortHeader label="Scope" col="partner_path" sort={props.sort} onSort={props.onSort} />
            <SortHeader
              label="Last rotated"
              col="last_rotated_at"
              sort={props.sort}
              onSort={props.onSort}
            />
            <th className="px-3 py-2 text-right">Bindings</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {props.items.map((c) => (
            <CredentialRow
              key={c.id}
              cred={c}
              onRotate={() => props.onRotate(c)}
              onReveal={() => props.onReveal(c)}
              onOpenBindings={() => props.onOpenBindings(c)}
              onMutated={props.onMutated}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SortHeader(props: {
  label: string;
  col: SortColumn;
  sort: SortState;
  onSort: (s: SortState) => void;
}): JSX.Element {
  const active = props.sort.col === props.col;
  const Icon = !active ? ArrowUpDown : props.sort.dir === "asc" ? ArrowUp : ArrowDown;
  // aria-sort exposes the current sort state to screen readers so
  // they can announce "column sorted ascending/descending".
  const ariaSort = !active ? "none" : props.sort.dir === "asc" ? "ascending" : "descending";
  return (
    <th className="px-3 py-2 text-left" aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() =>
          props.onSort({
            col: props.col,
            dir: active && props.sort.dir === "asc" ? "desc" : "asc",
          })
        }
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground",
          active && "text-foreground",
        )}
      >
        {props.label}
        <Icon className="h-3 w-3" aria-hidden />
      </button>
    </th>
  );
}

/**
 * Only resolve the underlying ref when it scrolls into view. Used to
 * defer the per-row bindings-count fetch so the initial page load
 * doesn't issue N parallel requests for rows that aren't visible.
 * Returns true once the element has entered the viewport at least
 * once (we don't re-toggle on exit - React Query's cache keeps the
 * count once loaded).
 */
function useVisibilityOnce<T extends Element>(): {
  ref: (node: T | null) => void;
  visible: boolean;
} {
  const [visible, setVisible] = useState(false);
  const elRef = useRef<T | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = (node: T | null): void => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    elRef.current = node;
    if (!node || visible) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            observerRef.current = null;
            return;
          }
        }
      },
      { rootMargin: "200px" },
    );
    obs.observe(node);
    observerRef.current = obs;
  };

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
    },
    [],
  );

  return { ref, visible };
}

function CredentialRow(props: {
  cred: ProviderCredential;
  onRotate: () => void;
  onReveal: () => void;
  onOpenBindings: () => void;
  onMutated: () => void;
}): JSX.Element {
  const { cred } = props;
  const [error, setError] = useState<string | null>(null);
  const profile = useRoleProfile();
  // Rotate / delete / reveal all 403 without require_write on the
  // credential's partner path. Hide the icon buttons entirely.
  // Reveal is technically require_read but we piggyback on write
  // here since read-only admins don't generally have a reason to
  // expose the last-4 masks (still an audit trail entry).
  const canWrite = hasWriteOn(profile, cred.partner.path);
  const softDelete = useMutation({
    mutationFn: (reason: string) => apiClient.deleteProviderCredential(cred.id, reason),
    onSuccess: () => {
      setError(null);
      props.onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  // Row-level bindings count. Re-uses the same reverse-index
  // endpoint the old card used and derives the inline count from
  // `total` on the response. We defer the fetch via
  // IntersectionObserver so rows below the fold don't each fire a
  // request - keeps the initial page load bounded when the cred
  // list grows past ~20 rows. Click-through opens the drawer for
  // the full list.
  //
  // TODO(scale): if we grow past a few hundred creds per page, add
  // a count-only endpoint (HEAD /provider-credentials/{id}/bindings)
  // so the drawer drawer can render without pulling the full
  // bindings[] payload for each row.
  const { ref: rowRef, visible: rowVisible } = useVisibilityOnce<HTMLTableRowElement>();
  const bindingsCount = useQuery({
    queryKey: ["credential-bindings", cred.id],
    queryFn: () => apiClient.listCredentialBindings(cred.id),
    enabled: rowVisible,
    retry: false,
  });

  return (
    <>
      <tr ref={rowRef} className={cn("border-t", cred.is_deleted && "opacity-60")}>
        <td className="px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <KeyRound className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
            <span className="truncate font-medium">{cred.label}</span>
            {cred.is_deleted && (
              <span className="rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
                deleted
              </span>
            )}
          </div>
        </td>
        <td className="px-3 py-2">
          <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
            {cred.provider}
          </span>
        </td>
        <td className="px-3 py-2">
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[10px] font-medium",
              cred.environment === "production"
                ? "bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-100"
                : "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100",
            )}
          >
            {ENV_LABEL[cred.environment]}
          </span>
        </td>
        <td className="px-3 py-2">
          <span className="break-words font-mono text-[11px] text-muted-foreground">
            {cred.partner.path}
          </span>
        </td>
        <td className="px-3 py-2">
          <RotatedCell cred={cred} />
        </td>
        <td className="px-3 py-2 text-right">
          <button
            type="button"
            onClick={props.onOpenBindings}
            className="rounded-md border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
            title="Open bindings drawer"
            aria-label={
              !rowVisible || bindingsCount.isLoading
                ? `Open bindings drawer for ${cred.label} (count loading)`
                : `Open bindings drawer for ${cred.label} (${
                    bindingsCount.data?.total ?? 0
                  } bindings)`
            }
          >
            {!rowVisible || bindingsCount.isLoading ? "…" : (bindingsCount.data?.total ?? 0)}
          </button>
        </td>
        <td className="px-3 py-2">
          {!cred.is_deleted && canWrite && (
            <div className="flex items-center justify-end gap-1">
              <IconButton
                onClick={props.onReveal}
                title="Reveal masked"
                aria={`Reveal ${cred.label}`}
              >
                <Eye className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                onClick={props.onRotate}
                title="Rotate credential"
                aria={`Rotate ${cred.label}`}
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                onClick={() => {
                  const reason = window.prompt(
                    `Reason for deleting "${cred.label}" (required):`,
                    "",
                  );
                  if (reason === null) return;
                  if (!reason.trim()) {
                    setError("reason is required");
                    return;
                  }
                  softDelete.mutate(reason.trim());
                }}
                disabled={softDelete.isPending}
                title="Delete credential"
                aria={`Delete ${cred.label}`}
                danger
              >
                <Trash2 className="h-3.5 w-3.5" />
              </IconButton>
            </div>
          )}
        </td>
      </tr>
      {error && (
        <tr className="border-t">
          <td colSpan={7} className="px-3 py-1 text-xs text-destructive">
            {error}
          </td>
        </tr>
      )}
    </>
  );
}

function RotatedCell(props: { cred: ProviderCredential }): JSX.Element {
  const { cred } = props;
  if (!cred.last_rotated_at)
    return <span className="font-mono text-[11px] text-muted-foreground">-</span>;
  // Format "YYYY-MM-DD HH:MM" in the viewer's locale-insensitive form
  // (avoids SSR / hydration mismatches). The raw value is a UTC
  // ISO-ish string from Postgres - slice the date + first 5 of time.
  const date = cred.last_rotated_at.slice(0, 10);
  const time = cred.last_rotated_at.slice(11, 16);
  const by = cred.updated_by_user_email;
  return (
    <div className="flex flex-col font-mono text-[11px] text-muted-foreground leading-tight">
      <span>
        {date} {time}
      </span>
      {by && (
        <span className="truncate text-[10px]" title={`Rotated by ${by}`}>
          by {by}
        </span>
      )}
    </div>
  );
}

function IconButton(props: {
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  title: string;
  aria: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      title={props.title}
      aria-label={props.aria}
      className={cn(
        "rounded-md border p-1 text-muted-foreground hover:bg-muted disabled:opacity-40",
        props.danger &&
          "border-destructive/40 bg-destructive/5 text-destructive hover:bg-destructive/10",
      )}
    >
      {props.children}
    </button>
  );
}

function BindingsDrawer(props: { cred: ProviderCredential; onClose: () => void }): JSX.Element {
  const { cred } = props;
  const bindings = useQuery({
    queryKey: ["credential-bindings", cred.id],
    queryFn: () => apiClient.listCredentialBindings(cred.id),
    retry: false,
  });

  // Mirror ModalOverlay's conventions so the drawer behaves like
  // every other dim-out in the app:
  //   - portal to document.body (escapes containing blocks)
  //   - ESC to close
  //   - lock body scroll while open
  //   - aria-modal + dialog role
  //   - z-[60] (above sticky topbar)
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [props]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Bindings using ${cred.label}`}
      className="fixed inset-0 z-[60] flex justify-end"
    >
      {/* Backdrop scrim - separate aria-hidden div so the dialog
          itself carries no click handler (jsx-a11y compliance). Esc
          + close X still dismiss for keyboard users. */}
      <div aria-hidden="true" onClick={props.onClose} className="absolute inset-0 bg-black/20" />
      <aside className="relative z-10 flex h-full w-full max-w-md flex-col border-l bg-card shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b p-4">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold">Bindings using {cred.label}</h2>
            <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
              {cred.partner.path} · {cred.provider} · {ENV_LABEL[cred.environment]}
            </p>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close bindings drawer"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          {bindings.isLoading && <p className="text-xs text-muted-foreground">Loading bindings…</p>}
          {bindings.isError && (
            <p className="text-xs text-destructive">
              {bindings.error instanceof Error ? bindings.error.message : "Failed to load bindings"}
            </p>
          )}
          {bindings.data && bindings.data.total === 0 && (
            <p className="text-xs text-muted-foreground">
              No bindings pin this credential. Safe to delete.
            </p>
          )}
          {bindings.data && bindings.data.total > 0 && (
            <ul className="space-y-2">
              {bindings.data.bindings.map((b) => (
                <li key={b.id} className="rounded-md border bg-muted/20 p-2 text-xs">
                  <div className="font-mono">
                    {b.provider_slug}/{b.feature_path}
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    @ {b.scope_kind}
                    {b.scope_name ? ` · ${b.scope_name}` : ""}
                    {!b.is_enabled && " · disabled"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

// --------------------------------------------------------------------- //
// Create dialog
// --------------------------------------------------------------------- //

function CreateDialog(props: {
  partners: Partner[];
  defaultPartnerId: string;
  providers: ProviderCatalogEntry[];
  onClose: () => void;
  onMutated: () => void;
}): JSX.Element {
  const [partnerId, setPartnerId] = useState(props.defaultPartnerId);
  // Push 12b: credentials can live at partner / organization / location
  // scope. Before this the dialog only wired partner - the backend
  // accepted the other two (V0009) but the UI gave operators no way
  // to create an org- or location-scoped override.
  const [scopeKind, setScopeKind] = useState<"partner" | "organization" | "location">("partner");
  const [organizationId, setOrganizationId] = useState<string>("");
  const [locationId, setLocationId] = useState<string>("");
  // Org + location pickers are scoped to the chosen partner. Fetched
  // lazily so the dialog doesn't hit the API on every render of the
  // Credentials page.
  const orgsQuery = useQuery({
    queryKey: ["organizations", "for-cred-create", partnerId],
    queryFn: () => apiClient.listOrganizations({ partner_id: partnerId, limit: 200 }),
    enabled: !!partnerId && scopeKind !== "partner",
    staleTime: 60_000,
  });
  const locsQuery = useQuery({
    queryKey: ["locations", "for-cred-create", organizationId],
    queryFn: () => apiClient.listLocations({ organization_id: organizationId, limit: 200 }),
    enabled: !!organizationId && scopeKind === "location",
    staleTime: 60_000,
  });
  const orgOptions = (orgsQuery.data?.organizations ?? []).filter((o) => !o.is_deleted);
  const locOptions = (locsQuery.data?.locations ?? []).filter((l) => !l.is_deleted);
  // Reset the downstream selections when the operator changes partner
  // or scope - stale ids would silently POST a cross-tenant value
  // otherwise.
  useEffect(() => {
    setOrganizationId("");
    setLocationId("");
  }, [partnerId]);
  useEffect(() => {
    if (scopeKind === "partner") {
      setOrganizationId("");
      setLocationId("");
    } else if (scopeKind === "organization") {
      setLocationId("");
    }
  }, [scopeKind]);

  const [providerSlug, setProviderSlug] = useState<string>(props.providers[0]?.slug ?? "");
  const [environment, setEnvironment] = useState<ProviderEnvironment>("sandbox");
  const [label, setLabel] = useState("");
  // Form-mode values (typed): { field_key -> string | number | boolean }
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  // Raw-JSON fallback for providers without a schema.
  const [rawJson, setRawJson] = useState("{\n  \n}");
  const [useRaw, setUseRaw] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const selectedProvider = useMemo(
    () => props.providers.find((p) => p.slug === providerSlug) ?? null,
    [props.providers, providerSlug],
  );
  const schema = selectedProvider?.credential_schema ?? {};
  const hasSchema = hasProperties(schema);

  // Reset per-provider state whenever the user changes the selector -
  // stale field values from the prior provider would silently get
  // submitted otherwise.
  useEffect(() => {
    setFormValues({});
    setRawJson("{\n  \n}");
    setUseRaw(false);
  }, [providerSlug]);

  const create = useMutation({
    mutationFn: (body: ProviderCredentialCreateRequest) => apiClient.createProviderCredential(body),
    onSuccess: () => {
      setError(null);
      props.onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const submit = (): void => {
    if (!partnerId) return setError("partner is required");
    if (!label.trim()) return setError("label is required");
    if (!reason.trim()) return setError("reason is required");
    if (scopeKind !== "partner" && !organizationId) {
      return setError("organization is required for org/location-scoped credentials");
    }
    if (scopeKind === "location" && !locationId) {
      return setError("location is required for location-scoped credentials");
    }

    let credential: Record<string, unknown>;
    let masked: Record<string, unknown> | undefined;
    if (hasSchema && !useRaw) {
      const result = buildValuesFromSchema(schema, formValues);
      if (result.error) return setError(result.error);
      credential = result.payload;
      masked = result.masked;
    } else {
      try {
        credential = JSON.parse(rawJson);
      } catch (err) {
        return setError(`credential JSON: ${err instanceof Error ? err.message : String(err)}`);
      }
      if (!credential || typeof credential !== "object" || Array.isArray(credential)) {
        return setError("credential must be a JSON object");
      }
    }

    create.mutate({
      partner_id: partnerId,
      ...(scopeKind !== "partner" ? { organization_id: organizationId } : {}),
      ...(scopeKind === "location" ? { location_id: locationId } : {}),
      provider: providerSlug,
      environment,
      label: label.trim(),
      credential,
      ...(masked && Object.keys(masked).length > 0 ? { masked_summary: masked } : {}),
      reason: reason.trim(),
    });
  };

  return (
    <DialogShell title="New credential" onClose={props.onClose} onSubmit={submit}>
      <Field label="Partner">
        <select
          required
          value={partnerId}
          onChange={(e) => setPartnerId(e.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        >
          {props.partners.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </Field>

      {/* Scope selector - partner is the default and the most common
          case (one credential shared across every descendant). Org
          or location scope is for when a specific child brings its
          own ISO account and overrides the partner's credential. */}
      <Field label="Scope">
        <div className="flex flex-wrap gap-2">
          {(["partner", "organization", "location"] as const).map((k) => {
            const selected = scopeKind === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setScopeKind(k)}
                className={cn(
                  "flex-1 rounded-md border px-3 py-1.5 text-xs capitalize",
                  selected
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-muted-foreground/20 text-muted-foreground hover:bg-muted",
                )}
              >
                {k}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {scopeKind === "partner" &&
            "Every organization and location under this partner inherits this credential unless they override it."}
          {scopeKind === "organization" &&
            "This credential applies only to the selected organization and its locations. Overrides the partner's credential when a binding resolves."}
          {scopeKind === "location" &&
            "This credential applies only to the selected location. Overrides organization and partner credentials when a binding resolves."}
        </p>
      </Field>

      {scopeKind !== "partner" && (
        <Field label="Organization">
          <select
            required
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            <option value="">- Select organization -</option>
            {orgOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          {orgsQuery.isLoading && (
            <p className="mt-1 text-[11px] text-muted-foreground">Loading organizations…</p>
          )}
          {orgsQuery.isSuccess && orgOptions.length === 0 && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              No organizations under this partner. Create one first.
            </p>
          )}
        </Field>
      )}

      {scopeKind === "location" && (
        <Field label="Location">
          <select
            required
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            disabled={!organizationId}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-50"
          >
            <option value="">
              {organizationId ? "- Select location -" : "- Pick an organization first -"}
            </option>
            {locOptions.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          {locsQuery.isLoading && organizationId && (
            <p className="mt-1 text-[11px] text-muted-foreground">Loading locations…</p>
          )}
          {locsQuery.isSuccess && organizationId && locOptions.length === 0 && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              No locations under this organization.
            </p>
          )}
        </Field>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Provider">
          <select
            value={providerSlug}
            onChange={(e) => setProviderSlug(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            {props.providers.map((p) => (
              <option key={p.id} value={p.slug}>
                {p.display_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Environment">
          <select
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as ProviderEnvironment)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            {ENVIRONMENTS.map((env) => (
              <option key={env} value={env}>
                {ENV_LABEL[env]}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Label">
        <input
          type="text"
          required
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="Acme Downtown Sandbox"
        />
      </Field>

      {hasSchema && !useRaw && (
        <SchemaForm schema={schema} values={formValues} onChange={setFormValues} />
      )}
      {(!hasSchema || useRaw) && (
        <Field
          label={
            hasSchema
              ? "Credential JSON (advanced mode - unchecked = guided form)"
              : "Credential JSON"
          }
        >
          <textarea
            required
            spellCheck={false}
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            rows={6}
            className="w-full resize-y rounded-md border bg-background px-3 py-2 font-mono text-xs"
            placeholder={'{"merchant_id": "MID...", "api_key": "sk_..."}'}
          />
        </Field>
      )}
      {hasSchema && (
        <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            checked={useRaw}
            onChange={(e) => setUseRaw(e.target.checked)}
            className="h-3 w-3 accent-primary"
          />
          Advanced: edit raw JSON instead of guided form
        </label>
      )}

      <Field label="Reason">
        <input
          type="text"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="Initial onboarding"
        />
      </Field>

      <ReasonNotice />

      {error && (
        <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
      )}

      <DialogActions
        busy={create.isPending}
        disabled={!partnerId || !label.trim() || !reason.trim() || !providerSlug}
        submitLabel={create.isPending ? "Creating…" : "Create"}
        onCancel={props.onClose}
      />
    </DialogShell>
  );
}

// --------------------------------------------------------------------- //
// Rotate dialog
// --------------------------------------------------------------------- //

function RotateDialog(props: {
  cred: ProviderCredential;
  providers: ProviderCatalogEntry[];
  onClose: () => void;
  onMutated: () => void;
}): JSX.Element {
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [rawJson, setRawJson] = useState("{\n  \n}");
  const [useRaw, setUseRaw] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const catalogEntry = useMemo(
    () => props.providers.find((p) => p.slug === props.cred.provider) ?? null,
    [props.providers, props.cred.provider],
  );
  const schema = catalogEntry?.credential_schema ?? {};
  const hasSchema = hasProperties(schema);

  const rotate = useMutation({
    mutationFn: (body: ProviderCredentialRotateRequest) =>
      apiClient.rotateProviderCredential(props.cred.id, body),
    onSuccess: () => {
      setError(null);
      props.onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const submit = (): void => {
    if (!reason.trim()) return setError("reason is required");

    let credential: Record<string, unknown>;
    let masked: Record<string, unknown> | undefined;
    if (hasSchema && !useRaw) {
      const result = buildValuesFromSchema(schema, formValues);
      if (result.error) return setError(result.error);
      credential = result.payload;
      masked = result.masked;
    } else {
      try {
        credential = JSON.parse(rawJson);
      } catch (err) {
        return setError(`credential JSON: ${err instanceof Error ? err.message : String(err)}`);
      }
      if (!credential || typeof credential !== "object" || Array.isArray(credential)) {
        return setError("credential must be a JSON object");
      }
    }

    rotate.mutate({
      credential,
      ...(masked && Object.keys(masked).length > 0 ? { masked_summary: masked } : {}),
      ...(newLabel.trim() ? { label: newLabel.trim() } : {}),
      reason: reason.trim(),
    });
  };

  return (
    <DialogShell title={`Rotate "${props.cred.label}"`} onClose={props.onClose} onSubmit={submit}>
      <div className="flex items-start gap-2 rounded-md bg-amber-100 p-3 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <div>
          Rotating soft-deletes the existing credential and writes a new active row. The old
          ciphertext remains in the audit trail. Make sure the new secret is valid with the provider
          before rotating.
        </div>
      </div>

      {hasSchema && !useRaw && (
        <SchemaForm schema={schema} values={formValues} onChange={setFormValues} />
      )}
      {(!hasSchema || useRaw) && (
        <Field label={hasSchema ? "New credential JSON (advanced mode)" : "New credential JSON"}>
          <textarea
            required
            spellCheck={false}
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            rows={6}
            className="w-full resize-y rounded-md border bg-background px-3 py-2 font-mono text-xs"
            placeholder={'{"merchant_id": "MID...", "api_key": "sk_..."}'}
          />
        </Field>
      )}
      {hasSchema && (
        <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            checked={useRaw}
            onChange={(e) => setUseRaw(e.target.checked)}
            className="h-3 w-3 accent-primary"
          />
          Advanced: edit raw JSON instead of guided form
        </label>
      )}

      <Field label={`Label (defaults to "${props.cred.label}")`}>
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder={props.cred.label}
        />
      </Field>

      <Field label="Reason">
        <input
          type="text"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="Quarterly rotation"
        />
      </Field>

      <ReasonNotice />

      {error && (
        <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
      )}

      <DialogActions
        busy={rotate.isPending}
        disabled={!reason.trim()}
        submitLabel={rotate.isPending ? "Rotating…" : "Rotate"}
        onCancel={props.onClose}
      />
    </DialogShell>
  );
}

// --------------------------------------------------------------------- //
// Reveal dialog - fetches masked_summary + writes a reveal_masked audit.
// --------------------------------------------------------------------- //

function RevealDialog(props: { cred: ProviderCredential; onClose: () => void }): JSX.Element {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, unknown> | null>(null);

  const reveal = useMutation({
    mutationFn: (r: string) => apiClient.revealMaskedCredential(props.cred.id, r),
    onSuccess: (resp) => {
      setError(null);
      setRevealed(resp.masked_summary);
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <DialogShell
      title={`Reveal masked summary - ${props.cred.label}`}
      onClose={props.onClose}
      onSubmit={() => {
        if (!reason.trim()) {
          setError("reason is required");
          return;
        }
        reveal.mutate(reason.trim());
      }}
    >
      <div className="flex items-start gap-2 rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <div>
          Viewing the masked preview writes an access-log entry with your user id and the reason
          below. Plaintext is never disclosed through this route.
        </div>
      </div>

      <Field label="Reason">
        <input
          type="text"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={revealed !== null}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
          placeholder="Compliance audit"
        />
      </Field>

      {revealed !== null && (
        <div className="rounded-md bg-muted/40 p-3 text-xs">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-0.5">
            {Object.entries(revealed).map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-mono text-muted-foreground">{k}</dt>
                <dd className="break-words font-mono">{String(v ?? "")}</dd>
              </div>
            ))}
          </dl>
          {Object.keys(revealed).length === 0 && (
            <p className="text-muted-foreground">No masked summary recorded.</p>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
      )}

      <DialogActions
        busy={reveal.isPending}
        disabled={revealed !== null || !reason.trim()}
        submitLabel={revealed !== null ? "Revealed" : reveal.isPending ? "Revealing…" : "Reveal"}
        onCancel={props.onClose}
        cancelLabel={revealed !== null ? "Close" : "Cancel"}
      />
    </DialogShell>
  );
}

// --------------------------------------------------------------------- //
// Reusable dialog scaffolding
// --------------------------------------------------------------------- //

function DialogShell(props: {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <ModalOverlay ariaLabel={props.title} onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit();
        }}
        className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-t-xl border bg-card p-4 shadow-lg sm:rounded-xl sm:p-5"
      >
        <h2 className="text-sm font-semibold">{props.title}</h2>
        {props.children}
      </form>
    </ModalOverlay>
  );
}

function Field(props: { label: string; children: React.ReactNode }): JSX.Element {
  // Wrap the input in the <label> (implicit-association form) so screen
  // readers tie the label text to the inner control without needing a
  // shared htmlFor/id pair on every field.
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{props.label}</span>
      {props.children}
    </label>
  );
}

function DialogActions(props: {
  busy: boolean;
  disabled: boolean;
  submitLabel: string;
  onCancel: () => void;
  cancelLabel?: string;
}): JSX.Element {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={props.onCancel}
        className="rounded-md border px-3 py-1.5 text-xs"
      >
        {props.cancelLabel ?? "Cancel"}
      </button>
      <button
        type="submit"
        disabled={props.busy || props.disabled}
        className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {props.submitLabel}
      </button>
    </div>
  );
}

function ReasonNotice(): JSX.Element {
  return (
    <p className="text-[11px] text-muted-foreground">
      A non-empty reason is required on every mutation - it's stored in the provider-credential
      access log alongside your user id.
    </p>
  );
}
